/**
 * Platform helpers the shared kidney modules import above their IMPORTS-END
 * marker. Mobile has these in constants/fluid-presets.ts and
 * utils/optional-nutrients.ts; keep the behaviour identical.
 */

import type { KidneyProfile } from '@/types'
import { isDialysisCondition, isPeritonealType, isPreDialysisType } from '../../../shared/dialysis'

/** Fluid limit by CKD stage — mirrors constants/fluid-presets.ts getFluidLimitByStage (mobile). */
export function getFluidLimitByStage(condition?: string, dialysisType?: string): number {
  // 4.46 F7a: "Starting soon" (dialysis planned, not started) gets the Stage 5 limit.
  if (isDialysisCondition(condition) && isPreDialysisType(dialysisType)) return 1200
  // 4.48 (M7b): peritoneal dialysis has NO default fluid limit (0 = "no default").
  if (isDialysisCondition(condition) && isPeritonealType(dialysisType)) return 0
  switch (condition) {
    case 'ckd_stage_3':
    case 'ckd_stage_3a':
    case 'ckd_stage_3b':
      return 2000
    case 'ckd_stage_4':
      return 1500
    case 'ckd_stage_5':
      return 1200
    case 'dialysis':
    case 'on_dialysis':
    case 'ckd_stage_5_dialysis':
      return 1000
    case 'kidney_stones':
      return 3000
    default:
      // stage 1-2, transplant, prevention, unknown
      return 2500
  }
}

/**
 * Whether the user's fluid limit is a medical restriction (a cap — exceeding
 * it warrants a warning) or a hydration goal (a floor — more is fine or even
 * encouraged, e.g. kidney stones). Mirrors constants/fluid-presets.ts (mobile).
 *
 * Historical policy (4.41): in the Diary fluid tracker and the Progress fluid
 * trend, cap-vs-goal is a display MODE, not a target. It follows the CURRENT
 * profile (an explicit `nutrientTargets.fluid` shape — max → cap, min-only →
 * goal — else this function on the current condition) on every date, and so
 * does the range-floor line; a day's `targets` snapshot fixes the number only.
 * 4.44 R5: both doctor reports use the same rule (shared/fluidMode.ts) — cap
 * mode counts days over, goal mode days met, each against that day's own number.
 * Parity with mobile is asserted by __tests__/fluidDialysis441.test.ts.
 */
export function isFluidRestricted(condition?: string): boolean {
  switch (condition) {
    case 'ckd_stage_3':
    case 'ckd_stage_3a':
    case 'ckd_stage_3b':
    case 'ckd_stage_4':
    case 'ckd_stage_5':
    case 'ckd_stage_5_dialysis':
    case 'dialysis':
    case 'on_dialysis':
      return true
    default:
      return false
  }
}

// 4.35 stone predicates + tracking defaults — mirror utils/optional-nutrients.ts (mobile).
// Defaults are resolved at READ time and never materialised by another toggle's write: the
// server reads a stored `showOxalate: true` as the user's own opt-in to oxalate guidance.
type StoneFields = Pick<KidneyProfile, 'condition' | 'hasKidneyStones' | 'stoneType'>

/** Stone-only profile: relaxed limits, "not a CKD patient". Unchanged from 4.34. */
export function isStoneOnly(kidneyProfile: Partial<StoneFields> | null | undefined): boolean {
  return kidneyProfile?.condition === 'kidney_stones'
}

/** Anyone who gets stones — drives guidance and tracking defaults only, never limits or scoring. */
export function hasStones(kidneyProfile: Partial<StoneFields> | null | undefined): boolean {
  return isStoneOnly(kidneyProfile) || kidneyProfile?.hasKidneyStones === true
}

export function defaultShowOxalate(kidneyProfile: Partial<StoneFields> | null | undefined): boolean {
  return hasStones(kidneyProfile) && kidneyProfile?.stoneType !== 'uric_acid'
}

export function defaultShowCalcium(kidneyProfile: Partial<StoneFields> | null | undefined): boolean {
  return hasStones(kidneyProfile)
}
